import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import useSidePanel from "../../../shared/useSidePanel";
import {
  displayValue,
  formatAmount,
  getOperationHistory,
} from "../../mock/operationsJournalMock";
import { getJournalHistory } from "../../../../../../api/financeApi";
import { formatEntryDate } from "../../mapJournalEntry";
import { DATA_LAYERS } from "../../constants/dataLayers";
import CorrectionForm from "./CorrectionForm";
import { getAccountCode, getSubconto, MAX_SUBCONTO } from "../../mock/sourceData";
import "./OperationDetails.css";

const GENERAL_FIELDS = [
  { key: "date", label: "Дата" },
  { key: "document", label: "Документ" },
  { key: "organization", label: "Организация" },
  { key: "quantity", label: "Количество" },
  { key: "amount", label: "Сумма", type: "amount" },
  { key: "source", label: "Источник" },
  { key: "dataType", label: "Тип данных" },
  { key: "comment", label: "Комментарий" },
];

/** Поля стороны проводки; ключ в данных — field + Dt / Kt */
const SIDE_FIELDS = [
  { field: "account", label: { dt: "Счёт Дт", kt: "Счёт Кт" } },
  { field: "articleOpiu", label: "Статья ОПИУ" },
  { field: "cfoOpiu", label: "ЦФО ОПИУ" },
  { field: "articleDds", label: "Статья ДДС" },
  { field: "project", label: "Проект" },
  { field: "department", label: "Отделение" },
  { field: "counterparty", label: "Контрагент" },
  { field: "employee", label: "Сотрудник" },
  { field: "contract", label: "Договор" },
  { field: "nomenclature", label: "Номенклатура" },
  { field: "fixedAsset", label: "Основное средство" },
  { field: "intangibleAsset", label: "Нематериальный актив" },
  { field: "cashAccount", label: "Денежный счет" },
  { field: "workType", label: "Вид работ" },
];

const SIDES = [
  { id: "dt", suffix: "Dt", title: "Дебет" },
  { id: "kt", suffix: "Kt", title: "Кредит" },
];

const ORIGIN_BADGES = {
  rule: { label: "Правило", className: "ojd-badge--rule" },
  mapping: { label: "Маппинг", className: "ojd-badge--mapping" },
};

const isEmpty = (value) => value == null || value === "";

function DetailsRow({ label, value, origin }) {
  const badge = ORIGIN_BADGES[origin];
  return (
    <div className="ojd-row">
      <span className="ojd-row__label">{label}</span>
      <span className="ojd-row__value">
        <span className={isEmpty(value) ? "ojd-row__empty" : undefined}>
          {displayValue(value)}
        </span>
        {badge && !isEmpty(value) && (
          <span className={`ojd-badge ${badge.className}`}>{badge.label}</span>
        )}
      </span>
    </div>
  );
}

/** Исходная проводка из источника — только чтение, без правил и маппинга */
function SourceSections({ operation, unit, showEmpty }) {
  const sections = [
    {
      id: "document",
      title: "Документ",
      rows: [
        { label: "Документ", value: operation.document },
        { label: "Дата", value: operation.date },
        { label: "Организация", value: operation.organization },
        { label: "Содержание", value: operation.content },
        { label: "Регистратор", value: operation.registrator },
        { label: "Номер журнала", value: operation.journalNumber },
        { label: "Строка", value: operation.lineNo },
        { label: "Комментарий", value: operation.comment },
      ],
    },
    ...SIDES.map((side) => {
      const accountValue = operation[`account${side.suffix}`];
      const accountCode = operation.fromApi
        ? operation[`accountCode${side.suffix}`]
        : getAccountCode(accountValue);
      const subconto = operation.fromApi
        ? (operation[`subconto${side.suffix}`] || [])
            .filter(Boolean)
            .map((value) => ({ value }))
        : getSubconto(operation, side.suffix);
      const slots = showEmpty
        ? Array.from({ length: MAX_SUBCONTO }, (_, index) => subconto[index]?.value)
        : subconto.map((item) => item.value);
      const sideLabel = side.id === "dt" ? "Дт" : "Кт";
      return {
        id: side.id,
        title: operation.fromApi ? side.title : `${side.title} · Субконто`,
        rows: [
          {
            label: `Счёт ${sideLabel}`,
            value: accountCode || accountValue,
            hint: accountCode ? accountValue : undefined,
          },
          ...slots.map((value, index) => ({
            label: `Субконто ${index + 1} ${sideLabel}`,
            value,
          })),
          { label: "Отделение", value: operation[`department${side.suffix}`] },
          {
            label: "Валюта",
            value: operation[side.id === "dt" ? "currencyDt" : "currencyKt"],
          },
        ],
      };
    }),
    {
      id: "amount",
      title: "Сумма",
      rows: [
        { label: "Сумма", value: formatAmount(operation.amount, unit) },
        { label: "Валюта", value: operation.currencyCode },
        {
          label: "Сумма в валюте",
          value:
            operation.amountFx == null
              ? null
              : operation.amountFx.toLocaleString("ru-RU", {
                  maximumFractionDigits: 2,
                }),
        },
        { label: "Тип данных", value: operation.dataType },
      ],
    },
  ];

  return (
    <>
      <div className="ojd-readonly">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
          <rect x="2" y="5.25" width="8" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.1" />
          <path d="M4 5.25V3.8a2 2 0 014 0v1.45" stroke="currentColor" strokeWidth="1.1" />
        </svg>
        Только чтение · Источник: <strong>{operation.source}</strong>
      </div>

      {sections.map((section) => {
        const rows = section.rows.filter((item) => showEmpty || !isEmpty(item.value));
        if (!rows.length) return null;
        return (
          <section key={section.id} className="ojd-section">
            <h3 className="ojd-section__subtitle">{section.title}</h3>
            {rows.map((item) => (
              <div key={item.label} className="ojd-row" title={item.hint}>
                <span className="ojd-row__label">{item.label}</span>
                <span className="ojd-row__value">
                  <span className={isEmpty(item.value) ? "ojd-row__empty" : undefined}>
                    {displayValue(item.value)}
                  </span>
                </span>
              </div>
            ))}
          </section>
        );
      })}
    </>
  );
}

function OperationDetails({
  operation,
  unit,
  dataLayer,
  editor,
  authorName,
  dictionaryByField,
  onPersistCorrection,
  readOnly = false,
  onClose,
}) {
  const [activeTab, setActiveTab] = useState("data");
  const [layer, setLayer] = useState(dataLayer);
  const [showEmpty, setShowEmpty] = useState(false);
  const [apiHistory, setApiHistory] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const isOpen = Boolean(operation);
  const headerOffset = useSidePanel(isOpen, onClose);

  useEffect(() => {
    setActiveTab("data");
    setLayer(dataLayer);
    setApiHistory(null);
    setHistoryError(null);
  }, [operation?.id, dataLayer]);

  useEffect(() => {
    if (!operation?.fromApi) return undefined;
    const controller = new AbortController();
    getJournalHistory(operation.id, controller.signal)
      .then((items) => setApiHistory(Array.isArray(items) ? items : []))
      .catch((error) => {
        if (error.name === "AbortError") return;
        setHistoryError(error.message || "Не удалось загрузить историю");
      });
    return () => controller.abort();
  }, [operation?.id, operation?.fromApi]);

  if (!operation) return null;

  const layerSnapshot = operation.layers?.[layer];
  const shown = layerSnapshot
    ? {
        ...operation,
        ...layerSnapshot,
        id: operation.id,
        document: operation.document,
        source: operation.source,
        dataType: operation.dataType,
        fromApi: true,
      }
    : operation;
  const baseLayer = operation.layers?.management?.accountDt
    ? operation.layers.management
    : operation.layers?.source;
  const correctionBase = baseLayer
    ? {
        ...operation,
        ...baseLayer,
        id: operation.id,
        document: operation.document,
        source: operation.source,
        dataType: operation.dataType,
      }
    : operation;

  const generalRows = GENERAL_FIELDS.map((item) => ({
    ...item,
    value:
      item.type === "amount"
        ? formatAmount(shown[item.key], unit)
        : shown[item.key],
  })).filter((item) => showEmpty || !isEmpty(item.value));

  const history = operation.fromApi
    ? (apiHistory || []).map((event) => {
        const changedAt = event.changed_at ? new Date(event.changed_at) : null;
        const date = changedAt ? formatEntryDate(event.changed_at) : "";
        const time = changedAt
          ? changedAt.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })
          : "";
        return {
          id: event.id,
          title: event.field_name || "Изменение",
          details: [event.old_value, event.new_value]
            .map((value) => (value == null || value === "" ? "—" : String(value)))
            .join(" → ")
            .concat(event.reason ? ` · ${event.reason}` : ""),
          author: event.user_name || "—",
          date,
          time,
        };
      })
    : getOperationHistory(operation);
  const isCorrectionMode = activeTab === "data" && layer === "corrections";

  const layersControl = (
    <div className="ojd-layers">
      <span className="ojd-layers__label">Слои данных</span>
      <select
        className="ojd-layers__select"
        value={layer}
        disabled={readOnly}
        onChange={(event) => setLayer(event.target.value)}
      >
        {DATA_LAYERS.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
      {!isCorrectionMode && (
        <label className="ojd-layers__checkbox">
          <input
            type="checkbox"
            checked={showEmpty}
            onChange={(event) => setShowEmpty(event.target.checked)}
          />
          <span>Отображать пустые поля</span>
        </label>
      )}
    </div>
  );

  const leaveCorrectionMode = () => {
    if (dataLayer === "corrections") onClose();
    else setLayer(dataLayer);
  };

  return createPortal(
    <div
      className="ojd-overlay"
      style={{ top: headerOffset }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside
        className="ojd-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ojd-title"
      >
        <header className="ojd-header">
          <div className="ojd-header__main">
            <h2 id="ojd-title" className="ojd-header__title">
              {shown.document}
              {shown.hasWarning && (
                <span
                  className="ojd-header__warning"
                  title={shown.warningTitle || "Требует внимания"}
                />
              )}
            </h2>
            <p className="ojd-header__subtitle">
              {shown.date} · {shown.organization}
            </p>
            <div className="ojd-header__summary">
              <span className="ojd-header__amount">
                {formatAmount(shown.amount, unit)}
              </span>
              <span className="ojd-tag ojd-tag--source">{shown.source}</span>
              <span className="ojd-tag">{shown.dataType}</span>
            </div>
          </div>
          <button
            type="button"
            className="ojd-close"
            aria-label="Закрыть"
            onClick={onClose}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1.5 1.5L12.5 12.5M12.5 1.5L1.5 12.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="ojd-tabs" role="tablist">
          {[
            { id: "data", label: "Данные" },
            { id: "history", label: "История" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`ojd-tabs__tab${activeTab === tab.id ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isCorrectionMode ? (
          <CorrectionForm
            key={operation.id}
            operation={correctionBase}
            unit={unit}
            editor={editor}
            authorName={authorName}
            layersControl={layersControl}
            dictionaryByField={dictionaryByField}
            onPersist={onPersistCorrection}
            onCancel={leaveCorrectionMode}
            onSaved={leaveCorrectionMode}
          />
        ) : (
        <>
        <div className="ojd-body">
          {activeTab === "data" ? (
            <>
              {layersControl}

              {layer === "source" ? (
                <SourceSections operation={shown} unit={unit} showEmpty={showEmpty} />
              ) : (
              <>
              <section className="ojd-section">
                <h3 className="ojd-section__title">Общие данные</h3>
                {generalRows.map((item) => (
                  <DetailsRow key={item.key} label={item.label} value={item.value} />
                ))}
              </section>

              {SIDES.map((side) => {
                const rows = SIDE_FIELDS.map((item) => {
                  const key = `${item.field}${side.suffix}`;
                  return {
                    key,
                    label:
                      typeof item.label === "string" ? item.label : item.label[side.id],
                    value: shown[key],
                    origin: shown.origins?.[key],
                  };
                }).filter((item) => showEmpty || !isEmpty(item.value));

                return (
                  <section key={side.id} className="ojd-section">
                    <h3 className="ojd-section__subtitle">{side.title}</h3>
                    {rows.length === 0 ? (
                      <div className="ojd-section__empty">Аналитики не заполнены</div>
                    ) : (
                      rows.map((item) => (
                        <DetailsRow
                          key={item.key}
                          label={item.label}
                          value={item.value}
                          origin={item.origin}
                        />
                      ))
                    )}
                  </section>
                );
              })}
              </>
              )}
            </>
          ) : (
            <ol className="ojd-history">
              {historyError && <li className="ojd-history__item">{historyError}</li>}
              {!historyError && operation.fromApi && apiHistory == null && (
                <li className="ojd-history__item">Загрузка истории...</li>
              )}
              {history.length === 0 && !historyError && apiHistory != null && (
                <li className="ojd-history__item">История изменений пуста</li>
              )}
              {history.map((event) => (
                <li key={event.id} className="ojd-history__item">
                  <span className="ojd-history__dot" />
                  <div className="ojd-history__content">
                    <div className="ojd-history__title">{event.title}</div>
                    {event.details && (
                      <div className="ojd-history__details">{event.details}</div>
                    )}
                    <div className="ojd-history__meta">
                      {event.author} · {event.date} {event.time}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        {!readOnly && (
          <footer className="ojd-footer">
            <button
              type="button"
              className="ojd-footer__primary"
              onClick={() => {
                setActiveTab("data");
                setLayer("corrections");
              }}
            >
              Создать корректировку
            </button>
          </footer>
        )}
        </>
        )}
      </aside>
    </div>,
    document.body
  );
}

export default OperationDetails;
